import { useState } from "react";
import { Search, X, FileSpreadsheet, Loader2 } from "lucide-react";
import Loading from "../../components/common/LoaderCommon";
import PaginationComponent from "../../components/PaginationComponent";
import { useGetBatchProfitReport, useDownloadBatchProfitReportExcel } from "../../service/reportsService";

const fmt = (val: number) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(val);

function BatchProfitReport() {
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + "01";

  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [purchaseNumber, setPurchaseNumber] = useState("");

  const branchId = Number(localStorage.getItem("branchId")) || 0;

  const { data, isLoading } = useGetBatchProfitReport({
    pageNo: page,
    pageSize,
    startDate,
    endDate,
    branchId,
    invoiceNumber: invoiceNumber || undefined,
    purchaseNumber: purchaseNumber || undefined,
  });

  const rows = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;

  const hasFilters =
    !!invoiceNumber ||
    !!purchaseNumber ||
    startDate !== firstOfMonth ||
    endDate !== today;

  const handleReset = () => {
    setStartDate(firstOfMonth);
    setEndDate(today);
    setInvoiceNumber("");
    setPurchaseNumber("");
    setPage(1);
  };

  const { mutate: downloadExcel, isPending: isDownloading } = useDownloadBatchProfitReportExcel();

  const handleExport = () => {
    downloadExcel({
      pageNo: 0,
      pageSize: 1000,
      startDate,
      endDate,
      branchId,
      invoiceNumber: invoiceNumber || undefined,
      purchaseNumber: purchaseNumber || undefined,
    });
  };

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-[#52627C] mb-1 block">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-[#52627C] mb-1 block">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-[#52627C] mb-1 block">
              Invoice No.
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A9BB5]" />
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => {
                  setInvoiceNumber(e.target.value);
                  setPage(1);
                }}
                placeholder="SAL-0001"
                className="h-11 w-[160px] rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] pl-10 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-[#52627C] mb-1 block">
              Purchase No.
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A9BB5]" />
              <input
                type="text"
                value={purchaseNumber}
                onChange={(e) => {
                  setPurchaseNumber(e.target.value);
                  setPage(1);
                }}
                placeholder="PUR-0001"
                className="h-11 w-[160px] rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] pl-10 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
              />
            </div>
          </div>
          {hasFilters && (
            <button
              type="button"
              onClick={handleReset}
              className="h-11 rounded-[12px] border border-[#E5ECF8] px-5 text-[#52627C] font-medium text-sm flex items-center gap-2 hover:bg-[#F5F8FD] transition-colors"
            >
              <X className="w-4 h-4" />
              Reset
            </button>
          )}
          
          <div className="ml-auto flex items-center">
            <button
              type="button"
              disabled={isDownloading}
              onClick={handleExport}
              className="h-11 rounded-[12px] bg-[#1A5EF9] px-5 text-white font-medium text-sm flex items-center gap-2 hover:bg-[#0E4FDE] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              Export to Excel
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
        <div className="flex items-center justify-between border-b border-[#E8EEF9] px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-[#21314A]">
              Batch-wise Profit
            </h3>
            <p className="text-sm text-[#7D8998] mt-0.5">
              <span className="font-semibold text-[#253650]">
                {totalElements}
              </span>{" "}
              entries found
            </p>
          </div>
          <PaginationComponent
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>

        {isLoading ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <Loading />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-[#8D98A7]">
            No batch profit data found for the selected filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-[#F5F8FD] text-left text-xs font-semibold text-[#31425E] uppercase tracking-wide">
                  <th className="px-5 py-3">Sales No.</th>
                  <th className="px-5 py-3">Sales Date</th>
                  <th className="px-5 py-3">Customer</th>
                  <th className="px-5 py-3">Product</th>
                  <th className="px-5 py-3">Purchase No.</th>
                  <th className="px-5 py-3 text-right">Sold Qty</th>
                  <th className="px-5 py-3 text-right">Returned</th>
                  <th className="px-5 py-3 text-right">Net Qty</th>
                  <th className="px-5 py-3 text-right">Cost/Unit</th>
                  <th className="px-5 py-3 text-right">Sale/Unit</th>
                  <th className="px-5 py-3 text-right">Profit/Unit</th>
                  <th className="px-5 py-3 text-right">Total Profit</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr
                    key={`${row.salesNumber}-${row.productId}-${idx}`}
                    className="border-t border-[#EEF2FA] text-sm text-[#43536A] hover:bg-[#FAFCFF] transition-colors"
                  >
                    <td className="px-5 py-4 font-medium text-[#1A5EF9]">
                      {row.salesNumber}
                    </td>
                    <td className="px-5 py-4">{row.salesDate}</td>
                    <td className="px-5 py-4">{row.customerName}</td>
                    <td className="px-5 py-4 font-medium text-[#1A2B4A]">
                      {row.productName}
                    </td>
                    <td className="px-5 py-4 text-[#52627C]">
                      {row.purchaseNumber}
                    </td>
                    <td className="px-5 py-4 text-right">
                      {fmt(row.soldQuantityFromBatch)}
                    </td>
                    <td className="px-5 py-4 text-right text-red-500">
                      {row.returnedQuantityFromBatch > 0
                        ? fmt(row.returnedQuantityFromBatch)
                        : "—"}
                    </td>
                    <td className="px-5 py-4 text-right font-medium">
                      {fmt(row.netSoldQuantityFromBatch)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      {fmt(row.purchaseUnitCost)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      {fmt(row.netSaleUnitPrice)}
                    </td>
                    <td className="px-5 py-4 text-right text-[#0E9F6E] font-medium">
                      {fmt(row.profitPerUnit)}
                    </td>
                    <td className="px-5 py-4 text-right font-bold text-[#0E9F6E]">
                      {fmt(row.totalProfit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default BatchProfitReport;
